import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Megaphone, Globe, Loader2, RotateCcw, CheckCircle2, Clock3, AlertTriangle, Trash2 } from "lucide-react";
import { AppNav } from "@/components/AppNav";
import { SpotEditor } from "@/components/SpotEditor";
import { api, apiError, mediaUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const STATE = {
  draft: ["Brouillon", "chip"], pending_payment: ["Brouillon · paiement non finalisé", "chip"], paid: ["Payé", "chip chip-fb"],
  rendering: ["En fabrication", "chip chip-fb"], ready: ["Prêt", "chip chip-live"], failed: ["Échec — relance gratuite", "chip chip-warn"],
};

function UrlForm({ onSpot }) {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async (e) => {
    e.preventDefault();
    if (url.trim().length < 4) return toast.error("Collez l'adresse d'un site");
    setBusy(true);
    try { onSpot(await api.spotAnalyze(url.trim())); } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={go} className="card-surface p-6 space-y-3" data-testid="spot-url-form">
      <label className="text-sm font-semibold" htmlFor="spot-url">Adresse du site à promouvoir</label>
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Globe className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8a8d91]" />
          <input id="spot-url" value={url} onChange={e => setUrl(e.target.value)} placeholder="www.mon-restaurant.fr" disabled={busy}
            className="w-full rounded-lg border border-[#ccd0d5] bg-white pl-9 pr-3 py-3 text-sm outline-none focus:border-[#1877f2]" data-testid="spot-url-input" />
        </div>
        <button type="submit" className="btn-primary justify-center !py-3" disabled={busy} data-testid="spot-analyze-btn">
          {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Visite du site…</> : "Analyser le site"}
        </button>
      </div>
      <p className="text-xs text-[#8a8d91]">BuzzlyMe récupère les photos, le logo et les textes des sites publics. Vous pourrez tout vérifier avant de payer.</p>
    </form>
  );
}

function SpotRow({ s, onOpen, onChange }) {
  const [busy, setBusy] = useState(false);
  const [label, cls] = STATE[s.status] || [s.status, "chip"];
  const retry = async () => {
    setBusy(true);
    try { onChange(await api.spotRetry(s.id)); toast.success("Spot relancé gratuitement"); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const thumb = s.photos.find(p => p.selected)?.url;
  return (
    <li className="flex items-center gap-3 py-3" data-testid={`spot-row-${s.id}`}>
      {thumb ? <img src={mediaUrl(thumb)} alt="" className="w-12 h-12 rounded-lg object-cover" /> : <span className="w-12 h-12 rounded-lg bg-[#f0f2f5]" />}
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm truncate">{s.site_name}</div>
        <div className="text-xs text-[#65676b]">{s.duration} s · <span className={cls}>{label}</span></div>
      </div>
      {s.status === "failed" && s.paid && <button type="button" className="btn-ghost !py-1.5" disabled={busy} onClick={retry} data-testid={`spot-retry-${s.id}`}><RotateCcw className="w-4 h-4" /> Relancer</button>}
      {["draft", "pending_payment"].includes(s.status) && <>
        <button type="button" className="btn-ghost !py-1.5" onClick={() => onOpen(s)} data-testid={`spot-open-${s.id}`}>Reprendre</button>
        <button type="button" className="btn-ghost !py-1.5 !px-2" title="Supprimer le brouillon" data-testid={`spot-delete-${s.id}`}
          onClick={() => window.confirm("Supprimer ce brouillon ?") && api.spotDelete(s.id).then(() => onChange()).catch(e => toast.error(apiError(e)))}>
          <Trash2 className="w-4 h-4" />
        </button>
      </>}
      {s.status === "ready" && <Link to="/app" className="btn-ghost !py-1.5" data-testid={`spot-view-${s.id}`}>Voir</Link>}
    </li>
  );
}

export default function SpotPage() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [spot, setSpot] = useState(null);
  const [list, setList] = useState([]);
  const loadList = useCallback(() => api.spots().then(setList).catch(() => {}), []);

  useEffect(() => {
    const sid = params.get("spot_id");
    const session = params.get("session_id");
    if (sid && session) {
      api.spotConfirm(sid, session).then(s => {
        if (s.payment === "pending") toast.message("Paiement en cours de validation…");
        else toast.success("Paiement reçu — votre spot est en fabrication (~1 min)");
        setParams({}, { replace: true });
        loadList();
      }).catch(e => { toast.error(apiError(e)); setParams({}, { replace: true }); });
    } else if (sid) {
      if (params.get("canceled")) toast.message("Paiement annulé — votre spot est gardé en brouillon");
      api.spotGet(sid).then(setSpot).catch(() => {});
      setParams({}, { replace: true });
    }
    loadList();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!list.some(s => ["rendering", "paid"].includes(s.status))) return undefined;
    const id = setInterval(loadList, 5000);
    return () => clearInterval(id);
  }, [list, loadList]);

  const updateSpot = (s) => { setSpot(["draft", "pending_payment"].includes(s.status) ? s : null); loadList(); };

  return (
    <>
      <AppNav current="spot" />
      <main className="max-w-3xl mx-auto px-4 lg:px-8 py-8 space-y-6" data-testid="spot-page">
        <div className="flex items-start gap-3">
          <span className="w-11 h-11 rounded-xl bg-[#1877f2] grid place-items-center shrink-0"><Megaphone className="w-5 h-5 text-white" /></span>
          <div>
            <h1 className="font-display text-3xl font-extrabold">Créer un spot publicitaire</h1>
            <p className="text-sm text-[#65676b] mt-1">Collez l'adresse d'un site : BuzzlyMe en fait une pub vidéo de 30 à 60 s avec ses photos, son logo et une voix off. <b>5 € le spot.</b></p>
          </div>
        </div>
        {spot ? <SpotEditor key={spot.id} spot={spot} setSpot={updateSpot} isAdmin={user?.role === "admin"} /> : <UrlForm onSpot={setSpot} />}
        {spot && <button type="button" className="text-sm text-[#65676b] hover:text-[#1877f2]" onClick={() => setSpot(null)} data-testid="spot-new-btn">← Analyser un autre site</button>}
        {list.length > 0 && (
          <section className="card-surface px-6 py-4" data-testid="spot-list">
            <h2 className="font-display font-bold flex items-center gap-2"><Clock3 className="w-4 h-4" /> Mes spots</h2>
            <ul className="divide-y divide-[#eef0f2]">{list.map(s => <SpotRow key={s.id} s={s} onOpen={setSpot} onChange={() => loadList()} />)}</ul>
            <p className="text-xs text-[#8a8d91] pt-2 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Les spots prêts sont dans la galerie du Dashboard, prêts à publier sur Facebook. <AlertTriangle className="w-3.5 h-3.5 ml-1" /> En cas d'échec, relance gratuite.</p>
          </section>
        )}
      </main>
    </>
  );
}
